/**
 * Anonymous access — guest session (device token) handling.
 *
 * Spec: docs/features/2026.08.28 - Anonymous access v1.md §3.
 *
 * A guest is identified by a server-issued device token (POST
 * /api/public/guest/session), stored in localStorage with a cookie mirror so
 * clearing one store doesn't lose the trip. The token rides on API calls as
 * `X-Guest-Token` (see ./api.ts) — never as a JWT, so every endpoint that
 * doesn't explicitly accept guests stays closed to them.
 *
 * At signup/login the token is sent along and the backend claims the guest's
 * trip into the real account; `clearGuestSession()` then retires it locally.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050/api';

const TOKEN_KEY = 'jodyGuestToken';
const TRIP_KEY = 'jodyGuestTripId';
const COOKIE_MAX_AGE_S = 365 * 24 * 60 * 60; // 1 year; trip retention is server-side

const readCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const writeCookie = (name: string, value: string) => {
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE_S}; SameSite=Lax`;
};

const eraseCookie = (name: string) => {
  document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
};

/** The stored guest token, healing whichever store lost it. */
export const getGuestToken = (): string | null => {
  const fromStorage = localStorage.getItem(TOKEN_KEY);
  const fromCookie = readCookie(TOKEN_KEY);
  const token = fromStorage || fromCookie;
  if (token) {
    if (!fromStorage) localStorage.setItem(TOKEN_KEY, token);
    if (!fromCookie) writeCookie(TOKEN_KEY, token);
  }
  return token;
};

export const hasGuestSession = (): boolean => !!getGuestToken();

export const setGuestTripId = (tripId: string) => localStorage.setItem(TRIP_KEY, tripId);
export const getGuestTripId = (): string | null => localStorage.getItem(TRIP_KEY);

/** Forget the guest identity locally (after claim, or on GUEST_INVALID). */
export const clearGuestSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TRIP_KEY);
  eraseCookie(TOKEN_KEY);
};

/**
 * Return the existing guest token or create a session on the server.
 * Called right before the first anonymous trip creation — never on page
 * load, so lookers and bots don't spawn guest rows.
 */
export const ensureGuestSession = async (): Promise<string> => {
  const existing = getGuestToken();
  if (existing) return existing;
  const response = await fetch(`${API_URL}/public/guest/session`, { method: 'POST' });
  if (!response.ok) {
    let message = 'Could not start planning. Please try again.';
    try {
      const body = await response.json();
      if (body?.error === 'rate_limited') message = 'One moment — please try again in a couple of seconds.';
      else if (body?.error === 'quota_exceeded') message = 'This network has reached its daily limit. Create a free account to keep planning.';
    } catch {
      /* keep default message */
    }
    throw new Error(message);
  }
  const body = await response.json();
  const token: string = body.guestToken;
  localStorage.setItem(TOKEN_KEY, token);
  writeCookie(TOKEN_KEY, token);
  return token;
};

// ---------------------------------------------------------------------------
// Signup wall — one global dialog (GuestWallHost, mounted in App), opened via
// a window event so any layer (pages, the API interceptor) can trigger it
// without prop-drilling or import cycles.
// ---------------------------------------------------------------------------

export const GUEST_WALL_EVENT = 'jody:guest-wall';

let lastWallOpenedAt = 0;

export const openGuestWall = (message?: string) => {
  lastWallOpenedAt = Date.now();
  window.dispatchEvent(new CustomEvent(GUEST_WALL_EVENT, { detail: { message } }));
};

/**
 * True right after the signup wall opened. The wall IS the feedback for a
 * gated action, so the page's own catch-block error toast ("Access token
 * required") must stay silent — pages wrap their showToast with this check.
 * The window is generous; the 401 rejection and the catch run milliseconds
 * apart in the same promise chain.
 */
export const guestWallJustOpened = (): boolean => Date.now() - lastWallOpenedAt < 1500;
